import { CalibrationPointsConfig } from 'features/calibration/sensors/types';
import { 
  createBasicFieldsObject, 
  createTemperatureFieldsObject, 
  clearCalibrationPointFields,
  BASIC_FIELDS,
  TEMPERATURE_FIELDS,
  ALL_KEYWORD_FIELDS
} from './fieldHelpers';


export type CalibrationFieldMap = Record<string, string>;

/** Floating-point tolerance for comparing calibration point keys. */
const EPSILON = 0.0001;


export const getFieldName = (
  point: number,
  sequence: 'inc' | 'dec' | 'inc2'
): string => {
  if (sequence === 'dec' && point === 0) {
    return 'point_0';
  }

  const pointStr = point.toString().replace(/\./g, '_');
  return `point_${pointStr}_${sequence}`;
};


export const initializeCalibrationDataFields = (
  points?: CalibrationPointsConfig
): CalibrationFieldMap => {

  const DEFAULT_POINTS: CalibrationPointsConfig = {
    increasing: [10, 20, 30, 40, 50],
    decreasing: [40, 30, 20, 10, 0],
    secondIncreasing: [10, 20, 30, 40, 50]
  };

  const calibrationPoints = points || DEFAULT_POINTS;

  const fields: CalibrationFieldMap = {
    sensorId: "",
    sensorType: "",
    category: "",
    modelFamily: "",
    model: "",
    range: "",
    calibrationType: "initial",
    calibrationFactor: "",
    temperatureCoefficient: "",
    baselineFrequency: "",
    notes: "",
    // Additional category-specific fields
    ambientTemp: "",
    atmPressure: "",
    dwtTraceable: "",
    ...createBasicFieldsObject(),
    ...createTemperatureFieldsObject(),
  };

  clearCalibrationPointFields(calibrationPoints, fields);

  return fields;
};

export const processKeyValue = (
  key: string,
  value: string,
  lineIndex: number,
  lines: string[],
  detectedPoints: CalibrationPointsConfig,
  updates: CalibrationFieldMap,
  maxPoint: number = Number.POSITIVE_INFINITY
): void => {

  const cleanKey = key.toLowerCase().replace(/\s+/g, "");
  const cleanValue = value.trim();

  
  const fixedFieldMap: Record<string, string> = {
    bol: BASIC_FIELDS[0],
    aol: BASIC_FIELDS[1],
    roomt: TEMPERATURE_FIELDS[0],
    roomf: TEMPERATURE_FIELDS[1],
    hight: TEMPERATURE_FIELDS[2],
    hightempf: TEMPERATURE_FIELDS[3],
  };

  if (fixedFieldMap[cleanKey]) {
    updates[fixedFieldMap[cleanKey]] = cleanValue;
    return;
  }

 
  const directMap: Record<string, string> = {
    abc: "calibrationFactor",
    calibrationfactor: "calibrationFactor",
    cde: "temperatureCoefficient",
    temperaturecoefficient: "temperatureCoefficient",
    fgh: "baselineFrequency",
    baselinefrequency: "baselineFrequency",
  };

  if (directMap[cleanKey]) {
    updates[directMap[cleanKey]] = cleanValue;
    return;
  }

  
  const numericKey = parseFloat(cleanKey);

  if (!isNaN(numericKey) && numericKey >= 0 && numericKey <= maxPoint + EPSILON) {

    const calibrationLineIndex = lines.slice(0, lineIndex).filter(l => {
      const match = l.match(/^([A-Za-z\s\d.]+)[\s\t:]+(\d+\.?\d*)$/);
      if (!match) return false;

      const k = match[1].trim().toLowerCase().replace(/\s+/g, "");
      const parsedKey = parseFloat(k);
      const tempKeywords = ALL_KEYWORD_FIELDS.map(t => t.toLowerCase());

      return (
        !tempKeywords.includes(k) &&
        !isNaN(parsedKey) &&
        parsedKey >= 0 &&
        parsedKey <= maxPoint + EPSILON
      );
    }).length;

    const incLen = detectedPoints.increasing.length || 5;
    const decLen = detectedPoints.decreasing.length || 5;

    let sequence: 'inc' | 'dec' | 'inc2' = 'inc2';

    if (calibrationLineIndex < incLen) sequence = 'inc';
    else if (calibrationLineIndex < incLen + decLen) sequence = 'dec';
    else sequence = 'inc2';

    const fieldName = getFieldName(numericKey, sequence);
    updates[fieldName] = cleanValue;
  }
};
